// externals
import type { Middleware } from 'redux';
import { nanoid } from 'nanoid';
import { sessionUtils } from '@makemydeal/dr-shared-ui-utils';

// types
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { ReduxStore } from '../types/middlewareTypes';

// telemetry
import { matchesIgnoredActionPrefix, TELEMETRY_IGNORED_ACTION_PREFIXES } from './telemetryIgnoredActions';

/**
 * PageAction name for the canonical redux-action "spine". Emitted by BOTH
 * emitters in this module — `createCorrelationMiddleware` (rich row, upstream of
 * `redux-api-middleware`) and `createReduxActionSpineMiddleware` (minimal row,
 * downstream of it, for the RSAA lifecycle FSAs correlationMiddleware never
 * sees). One constant so the two never drift on the event name.
 */
export const REDUX_ACTION_SPINE_EVENT = 'ReduxAction';

/**
 * Core join attributes shared by EVERY `ReduxAction` row regardless of which
 * emitter produced it. Extracting this keeps the join contract
 * (`correlationId` / `actionIndex` / `actionType` / `actionStatus` + store /
 * session / business context) defined in exactly one place, so the rich and
 * spine rows are guaranteed compatible for counting and cross-event joins.
 */
export type ReduxActionCoreAttrs = {
    storeId: string;
    sessionId: string;
    correlationId: string;
    actionIndex: number;
    actionType: string | undefined;
    actionStatus: number;
    businessContext: Record<string, unknown>;
};

export const buildReduxActionCoreAttrs = (input: ReduxActionCoreAttrs): Record<string, unknown> => ({
    storeId: input.storeId,
    sessionId: input.sessionId,
    correlationId: input.correlationId,
    actionIndex: input.actionIndex,
    actionType: input.actionType,
    actionStatus: input.actionStatus,
    ...input.businessContext
});

/**
 * Configuration interface for correlation middleware
 *
 * @template TState - The Redux state tree type
 */
export interface CorrelationConfig<TState> {
    /**
     * Whether telemetry is enabled. Can be:
     * - boolean: Static enable/disable (for buildtime config)
     * - function: Dynamic enable/disable based on state (for runtime feature toggles)
     *
     * When disabled, only minimal HTTP correlation is maintained for API debugging.
     * When enabled, full telemetry including New Relic tracking, metadata enrichment,
     * business context, and action hierarchy tracking is active.
     *
     * @example
     * // Runtime feature toggle configuration (recommended)
     * enabled: (state) => {
     *   try {
     *     return featureToggleSelectors.isReduxTelemetryEnabled(state);
     *   } catch (error) {
     *     return true; // Default to enabled during bootstrap
     *   }
     * }
     *
     * @example
     * // Static configuration (simple cases)
     * enabled: true
     */
    enabled?: boolean | ((state: TState) => boolean);

    /**
     * Identifier for the Redux store (e.g., 'dash-store', 'sales-checkin-store')
     * Used to distinguish which store an action originated from in telemetry.
     * This is especially useful when multiple stores are instrumented.
     *
     * @example
     * storeId: 'dash-store'
     */
    storeId: string;

    /**
     * Function to extract business context from state
     * Returns an object with business-specific correlation data (dealId, offerId, etc.)
     *
     * @param state - The current Redux state
     * @returns Object with business context key-value pairs
     */
    getBusinessContext: (state: TState) => Record<string, any>;

    /**
     * Action-type prefixes to skip (not enrich / not emit). Injectable so an
     * app/domain can widen or narrow the exclusion. Defaults to
     * {@link TELEMETRY_IGNORED_ACTION_PREFIXES} (the RSAA envelope only) —
     * navigation and store-init actions ARE emitted by default. See
     * telemetryIgnoredActions for the faithful-emit rationale.
     */
    ignoredActionPrefixes?: readonly string[];
}

// External action prefixes (should NOT be treated as child actions)
const EXTERNAL_ACTION_PREFIXES = [
    '@@router/', // React Router actions
    '@@redux/' // Redux internal actions
];

/**
 * Check if an action should be ignored (not enriched with correlation).
 *
 * Falsy-type policy is LOCAL to this middleware: a type-less action (e.g. an
 * RSAA envelope before redux-api-middleware expands it) is NOT ignored here so
 * it can still be classified. The shared prefix decision is delegated to
 * {@link matchesIgnoredActionPrefix}; `prefixes` is injectable via config (see
 * telemetryIgnoredActions for the faithful-emit philosophy).
 */
function shouldIgnoreAction(actionType: any, prefixes: readonly string[]): boolean {
    if (!actionType) {
        return false;
    }

    return matchesIgnoredActionPrefix(String(actionType), prefixes);
}

/**
 * RSAA Symbol constant for accessing RSAA action properties
 */
const RSAA = Symbol.for('@@redux-api-middleware/RSAA');

/**
 * Check if an action is an RSAA (Redux Standard API-calling Action)
 * Note: Scoped actions using spread operator convert Symbol keys to string keys,
 * so we need to check for both the Symbol and the string property.
 */
function isRSAAAction(action: any): boolean {
    /* istanbul ignore next: action validation tested through integration */
    if (!action || typeof action !== 'object') {
        return false;
    }
    // Check for Symbol key (original RSAA actions)
    const hasSymbol = RSAA in action;
    // Check for string key (scoped RSAA actions after spread operator)
    /* istanbul ignore next: string key fallback tested through integration */
    const hasString = '@@redux-api-middleware/RSAA' in action;
    return hasSymbol || hasString;
}

/**
 * Determine the category of action for telemetry classification
 * @returns 'Router' | 'Redux' | 'User'
 */
function getActionCategory(action: any): string {
    /* istanbul ignore next: action without type tested through integration */
    if (action?.type) {
        const typeString = String(action.type);
        /* istanbul ignore next: router category tested through integration */
        if (typeString.startsWith('@@router/')) {
            return 'Router';
        }
        /* istanbul ignore next: redux internal category tested through integration */
        if (typeString.startsWith('@@redux/')) {
            return 'Redux';
        }
    }

    return 'User';
}

/**
 * Automatically detect how an action was triggered and get browser interaction ID if available
 *
 * @returns Object with triggerSource and optional browserInteractionId
 * - triggerSource: 'user-interaction' | 'network' | 'router' | 'system-init' | 'async'
 * - browserInteractionId: Correlation ID for browser interaction (if available)
 *
 * Detection priority:
 * 1. RSAA actions → 'network'
 * 2. Router actions → 'router'
 * 3. Init/Bootstrap actions → 'system-init'
 * 4. Active browser interaction → 'user-interaction' (with browserInteractionId)
 * 5. Default → 'async' (setTimeout, promises, etc.)
 *
 * Note: We use setAttribute to test for active interactions since New Relic's get() method
 * is undocumented. During an active interaction (click, input), setAttribute succeeds.
 * Outside of one, it has no effect but doesn't throw.
 */
function detectTriggerContext(action: any): {
    triggerSource: string;
    browserInteractionId?: string;
} {
    // Helper to check if we're in an active New Relic browser interaction
    const hasActiveInteraction = (): boolean => {
        if (typeof window === 'undefined' || !window.newrelic?.interaction) {
            return false;
        }
        try {
            // Test if we're in an active interaction by trying to set an attribute
            // This works because setAttribute only succeeds during active interactions
            const interaction = window.newrelic.interaction();
            interaction.setAttribute('__corr_test__', Date.now());
            return true; // If we got here, we're in an active interaction
        } catch {
            return false;
        }
    };

    // 1. RSAA actions are always network-related (highest priority)
    if (isRSAAAction(action)) {
        if (hasActiveInteraction()) {
            // User-triggered API call (e.g., button click that makes request)
            return {
                triggerSource: 'network',
                browserInteractionId: `redux-corr-${Date.now()}`
            };
        }
        // Background/automatic API call
        /* istanbul ignore next: background network calls tested through integration */
        return { triggerSource: 'network' };
    }

    // 2. Router actions
    /* istanbul ignore next: router trigger tested through integration */
    if (action.type?.startsWith('@@router/')) {
        return { triggerSource: 'router' };
    }

    // 3. System initialization actions
    /* istanbul ignore next: init trigger tested through integration */
    if (action.type && (action.type.includes('INIT') || action.type.includes('BOOTSTRAP'))) {
        return { triggerSource: 'system-init' };
    }

    // 4. Check if we're inside a New Relic browser interaction (user click/input)
    if (hasActiveInteraction()) {
        // Direct user interaction (click, input, etc.)
        return {
            triggerSource: 'user-interaction',
            browserInteractionId: `redux-corr-${Date.now()}`
        };
    }

    // 5. Default: async (setTimeout, promises, etc.) - no active interaction
    return { triggerSource: 'async' };
}

/* istanbul ignore next: Utility functions for future enhancements - not currently used */
/**
 * Check if an action is external (router, redux internal)
 * External actions should NOT be treated as child actions even if dispatched
 * during middleware execution, as they represent external system events.
 *
 * NOTE: RSAA actions are NOT external - they should inherit parent context when
 * dispatched during action processing to show which action triggered the HTTP request.
 */
function _isExternalAction(action: any): boolean {
    // Only router and Redux internal actions are external
    // RSAA actions should inherit parent context to establish proper hierarchy
    if (action?.type) {
        const typeString = String(action.type);
        return EXTERNAL_ACTION_PREFIXES.some((prefix) => typeString.startsWith(prefix));
    }

    return false;
}

/* istanbul ignore next: Utility functions for future enhancements - not currently used */
/**
 * Extract parent correlation ID from action metadata
 */
function _getParentCorrelationId(action: any): string | undefined {
    return action?.meta?.originalAction?.meta?.correlationId;
}

/**
 * Creates a correlation middleware factory for Redux stores.
 *
 * This middleware automatically enriches Redux actions with correlation tracking metadata,
 * enabling end-to-end tracing across the application stack: User Interaction → Redux Action
 * → HTTP Request → BFF → Downstream services.
 *
 * **When telemetry is enabled** (default), actions are enriched with:
 * - correlationId: Unique ID for this action
 * - actionType: String version of action type
 * - timestamp: When action was dispatched
 * - sessionId: Application session ID
 * - Business context (dealId, offerId, etc. from getBusinessContext)
 * - parentCorrelationId: Parent action's correlationId (for action chains)
 *
 * **When telemetry is disabled**, middleware passes actions through without enrichment:
 * - Skips: Correlation context, New Relic tracking, metadata enrichment, business context
 * - HTTP interceptors must use explicit parentCorrelationId (from RSAA actions)
 * - Rationale: Avoids race conditions from global state, forces explicit parent specification
 *
 * @template TState - The Redux state tree type
 * @param config - Configuration object
 * @returns Redux middleware
 *
 * @example
 * // Runtime feature toggle configuration
 * const middleware = createCorrelationMiddleware({
 *   enabled: (state) => {
 *     try {
 *       return featureToggleSelectors.isReduxTelemetryEnabled(state);
 *     } catch (error) {
 *       return true; // Default to enabled during bootstrap
 *     }
 *   },
 *   getBusinessContext: (state) => ({
 *     dealXgId: state.offer.dealXgId,
 *     offerId: state.offerInfo.offerId
 *   })
 * });
 */

/**
 * Monotonic per-store actionIndex counters.
 *
 * IMPORTANT: This is intentionally module-scoped (not a closure variable inside
 * `createCorrelationMiddleware`). In scoped stores, `scopeMiddleware` re-invokes
 * the wrapped middleware's `(store) => (next) => (action)` chain on EVERY dispatch
 * (see `middlewareScopeWrapper.ts`), which would reset any closure-scoped counter
 * to 0 on every action — pinning `actionIndex` at 1. Keying off the stable
 * underlying store object survives that re-instantiation and yields a true 1..N
 * ordinal per store, while still isolating counts between independent stores
 * (e.g. across unit tests, each of which uses a fresh store object).
 */
const actionIndexCountersByStore = new WeakMap<object, { value: number }>();

/**
 * Resolve the stable WeakMap key for a store, consistent across BOTH the
 * middleware chain and the read-side hook.
 *
 * WHY the `getState` function (not the store object): redux's `applyMiddleware`
 * hands each middleware a `middlewareAPI` (`{ getState, dispatch }`), NOT the
 * store, so the middleware side keys off that. React components read the actual
 * Provider store via `useStore()` — a DIFFERENT object (in the scoped app a
 * `scopedStore` whose `__realStore` is the real store). The one identity shared
 * by all of them is the `getState` FUNCTION reference
 * (`middlewareAPI.getState === realStore.getState`), so keying off it makes the
 * middleware's per-store counters and the hook's reads resolve to the SAME
 * entry. Falls back to the (unwrapped) object when `getState` is absent (e.g.
 * plain mock stores in unit tests). See US2055631 (storeId/actionIndex on
 * `SelectorRead` were resolving to `'unknown'`/`0` before this fix).
 */
const resolveStoreKey = (store: unknown): object => {
    const host = ((store as { __realStore?: object }).__realStore ?? store) as { getState?: unknown };
    return (typeof host.getState === 'function' ? host.getState : host) as object;
};

/**
 * Resolve the stable per-store monotonic actionIndex counter for a (possibly
 * scoped) store. Scoped stores are recreated per dispatch but expose the stable
 * underlying store via `__realStore`; plain stores are already stable.
 *
 * Exported so the tail telemetry middlewares (`selectorDeltaTelemetryMiddleware`,
 * `stateSnapshotTelemetryMiddleware`) can draw from the SAME ordinal when they
 * have to backfill an actionIndex for an action that bypassed this middleware
 * (e.g. RSAA lifecycle FSAs dispatched by `redux-api-middleware` via `next()`,
 * downstream of correlationMiddleware). Sharing this counter keeps every
 * telemetry stream on a single monotonic per-store join key. See DE753626.
 */
export const getActionIndexCounter = (store: unknown): { value: number } => {
    const host = resolveStoreKey(store);
    let counter = actionIndexCountersByStore.get(host);
    if (!counter) {
        counter = { value: 0 };
        actionIndexCountersByStore.set(host, counter);
    }
    return counter;
};

/**
 * Per-store monotonic **emit sequence** — a second ordinal, distinct from
 * `actionIndex`, stamped at EMIT time (US2056149).
 *
 * WHY (Edge B — re-entrancy): `actionIndex` is drawn on the way DOWN the chain
 * (before `next()`), so it encodes **dispatch-initiation order**. When a
 * middleware dispatches a child BEFORE calling `next()` on its parent, the
 * child's reducer completes first, so reducer-completion order is the REVERSE of
 * `actionIndex` order for that pair. The tail emitters (`SelectorDelta`,
 * `StateSnapshot*`) run at the bottom of the chain in true completion order but
 * label rows with the initiation-order `actionIndex`; sorting purely by
 * `actionIndex` therefore scrambles reconstruction for re-entrant pairs.
 *
 * `nextEmitSequence(store)` is incremented and read at each `addPageAction`, so
 * ordering rows by `emitSequence` always recovers the true emission (≈ reducer-
 * completion) order while `actionIndex` stays the dispatch-initiation / causal
 * key. Per-store (keyed off the stable underlying store) — a cross-store global
 * clock is an explicit non-goal.
 */
const emitSequenceCountersByStore = new WeakMap<object, { value: number }>();

export const getEmitSequenceCounter = (store: unknown): { value: number } => {
    const host = resolveStoreKey(store);
    let counter = emitSequenceCountersByStore.get(host);
    if (!counter) {
        counter = { value: 0 };
        emitSequenceCountersByStore.set(host, counter);
    }
    return counter;
};

/** Draw the next per-store emit-sequence ordinal. Call at emit time. */
export const nextEmitSequence = (store: unknown): number => {
    const counter = getEmitSequenceCounter(store);
    counter.value += 1;
    return counter.value;
};

/**
 * Registry mapping a store (by stable underlying object) to its configured
 * `storeId`. Populated when `createCorrelationMiddleware` is instantiated so the
 * read-side `useTrackedSelector` hook (US2055631) — which only has the store
 * instance from `useStore()`, not the middleware config — can stamp `storeId`
 * on `SelectorRead` rows and key its change-only dedup per store.
 */
const storeIdByStore = new WeakMap<object, string>();

export const registerStoreId = (store: unknown, storeId: string): void => {
    const host = resolveStoreKey(store);
    storeIdByStore.set(host, storeId);
};

/** Resolve a store's configured `storeId`, or `'unknown'` if not registered. */
export const getStoreId = (store: unknown): string => {
    const host = resolveStoreKey(store);
    return storeIdByStore.get(host) ?? 'unknown';
};

/**
 * Per-action memo of a back-filled join `actionIndex` / `correlationId`.
 *
 * WHY (recommendation #1 — observe-only telemetry): the tail middlewares must
 * NOT mutate the action delivered to reducers just to share a join key. When an
 * action bypassed `correlationMiddleware` (RSAA lifecycle FSAs), both tail
 * middlewares call {@link resolveJoinActionIndex} / {@link resolveJoinCorrelationId}
 * with the SAME action object reference (the upstream middleware passes the
 * original action through untouched). Memoizing on that reference means the
 * first caller draws the next ordinal from the shared per-store counter and the
 * second caller reads the identical value — no injected `meta`, no reducer-
 * visible change, and no dependency on stamp ordering. Keyed in a WeakMap so
 * entries are collected with the (short-lived) action objects.
 */
const backfilledActionIndexByAction = new WeakMap<object, number>();
const backfilledCorrelationIdByAction = new WeakMap<object, string>();

/**
 * Resolve the join `actionIndex` for a dispatch WITHOUT mutating the action.
 *
 * Precedence:
 *   1. `action.meta.actionIndex` if `correlationMiddleware` already stamped it.
 *   2. A value memoized for this exact action object on a prior call this
 *      dispatch (the sibling tail middleware).
 *   3. A fresh ordinal drawn from the shared per-store counter, memoized on the
 *      action so the sibling tail middleware reads the same value.
 *
 * Type-less / primitive actions cannot key a WeakMap; they are non-joinable
 * anyway, so they get a bare counter draw.
 */
export const resolveJoinActionIndex = (store: unknown, action: unknown): number => {
    const stamped = (action as { meta?: { actionIndex?: number } } | null)?.meta?.actionIndex;
    if (stamped != null) return stamped;

    const counter = getActionIndexCounter(store);
    if (action == null || typeof action !== 'object') {
        counter.value += 1;
        return counter.value;
    }

    const existing = backfilledActionIndexByAction.get(action);
    if (existing != null) return existing;

    counter.value += 1;
    backfilledActionIndexByAction.set(action, counter.value);
    return counter.value;
};

/**
 * Resolve the join `correlationId` for a dispatch WITHOUT mutating the action.
 * Same precedence and memoization strategy as {@link resolveJoinActionIndex}.
 */
export const resolveJoinCorrelationId = (action: unknown): string => {
    const stamped = (action as { meta?: { correlationId?: string } } | null)?.meta?.correlationId;
    if (stamped != null) return stamped;

    if (action == null || typeof action !== 'object') {
        return nanoid();
    }

    const existing = backfilledCorrelationIdByAction.get(action);
    if (existing != null) return existing;

    const id = nanoid();
    backfilledCorrelationIdByAction.set(action, id);
    return id;
};

export function createCorrelationMiddleware<TState = any>(
    config: CorrelationConfig<TState>
): Middleware<Record<string, never>, TState> {
    const { enabled = true, storeId, getBusinessContext, ignoredActionPrefixes = TELEMETRY_IGNORED_ACTION_PREFIXES } = config;

    return (store) => {
        // Register this store's id so the read-side `useTrackedSelector` hook
        // (US2055631), which only sees the store instance, can resolve `storeId`
        // via `getStoreId(store)`. Idempotent under scopeMiddleware re-invocation.
        registerStoreId(store, storeId);

        // Per-store monotonic counter used as the join key across ReduxAction,
        // StateSnapshotDelta, SelectorDelta, and their companion events. Stamped
        // onto action.meta.actionIndex so downstream telemetry middlewares can
        // read it back rather than maintaining independent counters that drift.
        // Increments only for actions that are actually enriched (i.e., not
        // ignored, not null, and telemetry enabled).
        //
        // The counter itself lives in the module-scoped `actionIndexCountersByStore`
        // WeakMap (keyed off the stable underlying store) rather than in this
        // closure, because `scopeMiddleware` re-instantiates this closure on every
        // dispatch and would otherwise reset the counter to 0 each time.

        // Determine if telemetry is enabled (static or dynamic)
        const isEnabled = () => {
            if (typeof enabled === 'function') {
                return enabled(store.getState());
            }
            return enabled;
        };

        return (next) => (action) => {
            // When telemetry is disabled, use minimal HTTP-only middleware
            if (!isEnabled()) {
                // Context removed: HTTP interceptors should use explicit parentCorrelationId only
                // to avoid race conditions from global state
                return next(action);
            }

            // Full telemetry middleware when enabled
            // Handle null/undefined actions gracefully
            if (!action) {
                return next(action);
            }

            // Skip ignored actions
            if (shouldIgnoreAction(action.type, ignoredActionPrefixes)) {
                return next(action);
            }

            // Context removed: HTTP interceptors should use explicit parentCorrelationId only
            // to avoid race conditions from global state

            // Get current state for context
            const state = store.getState();

            // Always generate new correlationId for each dispatch
            const correlationId = nanoid();

            // Assign the next actionIndex for this dispatch. Ties ReduxAction,
            // ReduxActionComplete, StateSnapshotDelta, SelectorDelta, and any
            // legacy ReduxAction emitters together on a single per-store ordinal.
            // Uses the stable module-scoped counter so the ordinal survives
            // scopeMiddleware re-instantiating this closure on every dispatch.
            const actionIndexCounter = getActionIndexCounter(store);
            actionIndexCounter.value += 1;
            const actionIndex = actionIndexCounter.value;

            // Get business context from state
            const businessContext = getBusinessContext(state);

            // Determine action category for telemetry
            const actionCategory = getActionCategory(action);

            // Detect trigger source and browser interaction ID
            const { triggerSource, browserInteractionId } = detectTriggerContext(action);

            // For RSAA actions, extract endpoint as actionType for display
            let actionType: string | undefined;
            if (action.type) {
                actionType = String(action.type);
            } else if (isRSAAAction(action)) {
                // Try Symbol key first (original), then string key (after scoped action spread)
                const rsaaSymbol = Symbol.for('@@redux-api-middleware/RSAA');
                /* istanbul ignore next: RSAA config retrieval tested through integration */
                const rsaaConfig = action[rsaaSymbol] || action['@@redux-api-middleware/RSAA'];
                /* istanbul ignore next: RSAA endpoint extraction tested through integration */
                actionType = rsaaConfig?.endpoint ? `RSAA: ${rsaaConfig.endpoint}` : 'RSAA: [no endpoint]';
            } else {
                // Record action without type as New Relic event for production monitoring
                /* istanbul ignore next: actions without type tested through integration */
                try {
                    if (typeof window !== 'undefined' && window.newrelic?.addPageAction) {
                        window.newrelic.addPageAction('ReduxActionWithoutType', {
                            correlationId,
                            storeId,
                            actionKeys: Object.keys(action).join(','),
                            hasType: ('type' in action).toString(),
                            typeValue: action.type,
                            isFunction: (typeof action === 'function').toString(),
                            isRSAA: isRSAAAction(action).toString(),
                            timestamp: Date.now(),
                            sessionId: sessionUtils.getAppSessionId()
                        });
                    }
                } catch (error) {
                    // Silently fail - don't break app if New Relic unavailable
                }
            }

            // Enrich action with correlation metadata
            const enrichedAction = {
                ...action,
                meta: {
                    ...action.meta,
                    correlationId,
                    actionIndex,
                    actionType,
                    actionCategory,
                    triggerSource,
                    timestamp: Date.now(),
                    sessionId: sessionUtils.getAppSessionId(),
                    storeId,
                    ...businessContext,
                    ...(browserInteractionId && { browserInteractionId })
                }
            };

            /**
             * Set correlation context for HTTP interceptors
             *
             * CRITICAL: Different behavior for RSAA vs non-RSAA actions:
             *
             * RSAA Actions (Redux-API-Middleware):
             * ------------------------------------
             * RSAA actions dispatch HTTP requests, so we need a parent-child relationship:
             *   Redux RSAA → Ajax Request → BFF Request
             *
             * Problem: If we set correlationId in the context, the HTTP interceptor will REUSE it
             * (see axiosIntercept.ts: `const traceId = correlationCtx?.correlationId || generateUuid()`)
             * This causes RSAA and Ajax to share the same ID, appearing as siblings not parent-child.
             *
             * Solution: For RSAA actions, OMIT correlationId from context so HTTP interceptor
             * generates a fresh ID, but SET parentCorrelationId to the RSAA's correlationId.
             * This creates proper hierarchy:
             *   - RSAA action: correlationId="ABC123" (recorded in Redux telemetry)
             *   - Ajax request: correlationId="NEW456", parentCorrelationId="ABC123" (links to RSAA)
             *
             * Non-RSAA Actions (regular Redux actions):
             * -----------------------------------------
             * Regular actions don't fire HTTP requests, so we set correlationId normally.
             * If they DO trigger child actions, those children will use this correlationId as parent.
             */
            const isRSAA = isRSAAAction(action);
            const _contextToSet = isRSAA
                ? {
                      // RSAA: Don't set correlationId - forces HTTP interceptor to generate new UUID
                      // This prevents ID collision between RSAA action and its HTTP request
                      parentCorrelationId: enrichedAction.meta.correlationId, // RSAA's ID becomes parent for HTTP
                      actionType: enrichedAction.meta.actionType
                  }
                : {
                      // Non-RSAA: Set correlationId normally for regular Redux actions
                      correlationId: enrichedAction.meta.correlationId,
                      actionType: enrichedAction.meta.actionType,
                      ...(enrichedAction.meta
                          .parentCorrelationId && /* istanbul ignore next: parentCorrelationId tested through RSAA action tests */ {
                          parentCorrelationId: enrichedAction.meta.parentCorrelationId
                      })
                  };

            // Context removed: HTTP interceptors should use explicit parentCorrelationId only
            // to avoid race conditions from global state

            // Associate Redux actions with SPA interactions
            try {
                if (typeof window !== 'undefined' && window.newrelic?.interaction) {
                    const interaction = window.newrelic.interaction();
                    interaction.save();
                    interaction.setAttribute('reduxAction', enrichedAction.meta.actionType);
                }
            } catch (error) {
                // Silently fail - don't break app if New Relic unavailable
                /* istanbul ignore next: New Relic error handling tested through integration */
            }

            // Record Redux action as PageAction for New Relic visibility
            try {
                if (typeof window !== 'undefined' && window.newrelic?.addPageAction) {
                    window.newrelic.addPageAction(REDUX_ACTION_SPINE_EVENT, {
                        ...buildReduxActionCoreAttrs({
                            storeId,
                            sessionId: enrichedAction.meta.sessionId,
                            correlationId: enrichedAction.meta.correlationId,
                            actionIndex: enrichedAction.meta.actionIndex,
                            actionType: enrichedAction.meta.actionType,
                            actionStatus: enrichedAction.meta.actionStatus || 0, // Default to 0 (UNKNOWN) if not set
                            businessContext
                        }),
                        // Emit-time ordinal (US2056149). This row is emitted on the
                        // way DOWN (before next), so its emitSequence encodes
                        // dispatch-initiation order; the tail rows' emitSequence
                        // encodes reducer-completion order.
                        emitSequence: nextEmitSequence(store),
                        actionCategory: enrichedAction.meta.actionCategory,
                        triggerSource: enrichedAction.meta.triggerSource,
                        timestamp: enrichedAction.meta.timestamp,
                        /* istanbul ignore next: browserInteractionId spread tested through integration */
                        ...(enrichedAction.meta.browserInteractionId && {
                            browserInteractionId: enrichedAction.meta.browserInteractionId
                        }),
                        ...(enrichedAction.meta
                            .dispatchedBy && /* istanbul ignore next: dispatchedBy spread tested through integration */ {
                            dispatchedBy: enrichedAction.meta.dispatchedBy
                        })
                    });
                }
            } catch (error) {
                // Silently fail - don't break app if New Relic unavailable
                /* istanbul ignore next: New Relic error handling tested through integration */
            }

            // Capture start time for duration tracking
            const startTime = Date.now();

            // Helper function to send duration tracking event
            const sendDurationTracking = () => {
                const duration = Date.now() - startTime;

                try {
                    if (typeof window !== 'undefined' && window.newrelic?.addPageAction) {
                        window.newrelic.addPageAction('ReduxActionComplete', {
                            correlationId: enrichedAction.meta.correlationId,
                            actionIndex: enrichedAction.meta.actionIndex,
                            actionType: enrichedAction.meta.actionType,
                            duration,
                            timestamp: Date.now(),
                            sessionId: enrichedAction.meta.sessionId,
                            storeId,
                            ...businessContext,
                            ...(enrichedAction.meta
                                .parentCorrelationId && /* istanbul ignore next: parentCorrelationId spread tested through integration */ {
                                parentCorrelationId: enrichedAction.meta.parentCorrelationId
                            })
                        });
                    }
                } catch (error) {
                    // Silently fail - don't break app if New Relic unavailable
                    /* istanbul ignore next: New Relic error handling tested through integration */
                }
            };

            try {
                const result = next(enrichedAction);

                // Send duration tracking after successful processing
                sendDurationTracking();

                return result;
            } catch (error) {
                // Send duration tracking even if next() throws an error
                sendDurationTracking();

                // Re-throw the error to maintain expected behavior
                throw error;
            }
        };
    };
}

/**
 * Dispatches a child action with inherited correlation metadata from a parent action.
 *
 * This utility function enables middleware to dispatch derived actions while maintaining
 * the correlation chain. The child action carries `meta.originalAction` referencing its
 * parent, which the correlation middleware uses to derive the child's parentCorrelationId.
 *
 * **IMPORTANT**: This function ALWAYS adds `meta.originalAction` regardless of whether
 * telemetry is enabled or disabled. This preserves the semantic meaning of the utility
 * and avoids coupling it to the telemetry configuration. The correlation middleware will
 * process this metadata appropriately based on its own configuration.
 *
 * @param store - The Redux store instance
 * @param parentAction - The action that triggered this dispatch (with correlation metadata)
 * @param childAction - The action to dispatch with inherited correlation metadata
 *
 * @example
 * // In middleware, when dispatching a derived action:
 * case APPLY_ROLL_TO_OPTIONS:
 *   const result = await calculatePayment();
 *   dispatchWithTelemetry(store, action, {
 *     type: UPDATE_ROLL_TARGET_PAYMENT,
 *     payload: result
 *   });
 */
export const dispatchWithTelemetry = <TState = any>(store: ReduxStore<TState>, parentAction: AnyFSA, childAction: AnyFSA): void => {
    // Use the originalAction pattern that correlationMiddleware expects
    // This lets the middleware derive the child's parentCorrelationId from the parent action
    const enrichedChild = {
        ...childAction,
        meta: {
            ...childAction.meta,
            originalAction: parentAction
        }
    };

    store.dispatch(enrichedChild);
};

/**
 * Constants for action type patterns and classification
 */
export const ACTION_PATTERNS = {
    RSAA_PREFIX: 'RSAA:',
    REQUEST_SUFFIX: '_REQUEST',
    FAILURE_SUFFIX: '_FAILURE',
    FAILED_SUFFIX: '_FAILED',
    ERROR_SUFFIX: '_ERROR',
    PAYMENT_FAILED_SUFFIX: '_PAYMENT_FAILED',
    CLEAR_PREFIX: 'CLEAR',
    SET_PREFIX: 'SET'
} as const;

export const VALIDATION_ERROR_ACTIONS = {
    SET_FIELD_ERROR: 'SET_FIELD_ERROR',
    SET_ERROR_FIELDS: 'SET_ERROR_FIELDS',
    SET_ERROR_FIELDS_BY_CATEGORY: 'SET_ERROR_FIELDS_BY_CATEGORY'
} as const;

/**
 * HTTP-style status codes for Redux action classification.
 * Used by getActionStatus() and the correlation middleware.
 */
export const ACTION_STATUS = {
    UNKNOWN: 0,
    SUCCESS: 200,
    ASYNC_ACCEPTED: 202,
    VALIDATION_ERROR: 400,
    SYSTEM_ERROR: 500
} as const;

/**
 * Get HTTP-style status code for Redux action type
 *
 * Categorizes actions using HTTP status code semantics:
 * - 0: Unknown/Unresolved (edge cases - undefined, null, non-string)
 * - 200: Success (default - normal operations, updates, user interactions)
 * - 202: Async Accepted (actions ending in _REQUEST)
 * - 400: Validation Error (SET_FIELD_ERROR, SET_ERROR_FIELDS, SET_ERROR_FIELDS_BY_CATEGORY)
 * - 500: System Error (actions ending in _FAILURE, _FAILED, _ERROR excluding CLEAR_ and SET_ prefixes)
 *
 * @see US1815744 for full action classification details
 * @param actionType - The Redux action type to categorize
 * @returns HTTP-style status code (0, 200, 202, 400, or 500)
 */
export function getActionStatus(actionType: string): number {
    // Handle edge cases
    if (!actionType || typeof actionType !== 'string') {
        return ACTION_STATUS.UNKNOWN;
    }

    // 202: RSAA actions (synthetic format: "RSAA: <endpoint>")
    if (actionType.startsWith(ACTION_PATTERNS.RSAA_PREFIX)) {
        return ACTION_STATUS.ASYNC_ACCEPTED;
    }

    // 400: Validation errors
    if (
        actionType === VALIDATION_ERROR_ACTIONS.SET_FIELD_ERROR ||
        actionType === VALIDATION_ERROR_ACTIONS.SET_ERROR_FIELDS ||
        actionType === VALIDATION_ERROR_ACTIONS.SET_ERROR_FIELDS_BY_CATEGORY
    ) {
        return ACTION_STATUS.VALIDATION_ERROR;
    }

    // 500: System errors
    if (
        actionType.endsWith(ACTION_PATTERNS.FAILURE_SUFFIX) ||
        actionType.endsWith(ACTION_PATTERNS.FAILED_SUFFIX) ||
        (actionType.endsWith(ACTION_PATTERNS.ERROR_SUFFIX) &&
            !actionType.startsWith(ACTION_PATTERNS.CLEAR_PREFIX) &&
            !actionType.startsWith(ACTION_PATTERNS.SET_PREFIX))
    ) {
        return ACTION_STATUS.SYSTEM_ERROR;
    }

    // 202: Async requests
    if (actionType.endsWith(ACTION_PATTERNS.REQUEST_SUFFIX) || actionType === 'PAYMENT_REQUESTED') {
        return ACTION_STATUS.ASYNC_ACCEPTED;
    }

    // 200: Success (default for all known patterns)
    return ACTION_STATUS.SUCCESS;
}

/**
 * Safely emit a New Relic PageAction. Silent failure — never break the app.
 */
const safeEmitPageAction = (name: string, payload: Record<string, unknown>): void => {
    /* istanbul ignore next: window guard for SSR */
    if (typeof window === 'undefined') return;
    try {
        window.newrelic?.addPageAction?.(name, payload as Record<string, string | number>);
    } catch {
        // Telemetry loss is preferred over app breakage.
    }
};

/* istanbul ignore next: catch is defence-in-depth; getAppSessionId is a pure getter */
const safeGetSessionId = (): string => {
    try {
        return sessionUtils.getAppSessionId?.() ?? '';
    } catch {
        return '';
    }
};

/**
 * Configuration for the redux-action spine middleware — the DOWNSTREAM half of
 * the `ReduxAction` emitter pair (see {@link REDUX_ACTION_SPINE_EVENT}).
 *
 * @template TState - The Redux state tree type.
 */
export type ReduxActionSpineConfig<TState> = {
    /**
     * Whether the spine is active. Should be gated on the SAME toggle as
     * `createCorrelationMiddleware` (`enableReduxTelemetry`) so the two emitters
     * form one coherent `ReduxAction` stream: enabled → correlation emits the
     * rich rows and this fills the RSAA-lifecycle gap; disabled → neither emits.
     * A throwing predicate is treated as disabled.
     */
    enabled?: boolean | ((state: TState) => boolean);

    /** Identifier for the Redux store emitting these events (e.g. `'dash-store'`). */
    storeId: string;

    /**
     * Extract per-event business context (dealXgId, offerId, …) from state.
     * Should match `createCorrelationMiddleware`'s context so spine rows group
     * identically. Errors are swallowed.
     */
    getBusinessContext?: (state: TState) => Record<string, unknown>;

    /**
     * Action-type prefixes to skip. Injectable; defaults to
     * {@link TELEMETRY_IGNORED_ACTION_PREFIXES} (RSAA envelope only). Keep this
     * a subset of (or equal to) `correlationMiddleware`'s set so the spine
     * back-fills everything correlation skips and the aggregate `ReduxAction`
     * stays gap-free.
     */
    ignoredActionPrefixes?: readonly string[];
};

/**
 * Create the redux-action spine middleware — the DOWNSTREAM companion to
 * `createCorrelationMiddleware`.
 *
 * WHY IT EXISTS: `correlationMiddleware` must run UPSTREAM of
 * `redux-api-middleware` (it enriches the action `meta` that downstream
 * middleware and reducers read), so it never sees the RSAA lifecycle FSAs
 * (`OFFER_SAVE_SUCCESS`, `PAYMENT_RECEIVED/FAILURE`, …) that `redux-api-middleware`
 * dispatches via `next()`. No single chain position sees both the pre-enrichment
 * normal actions AND the lifecycle FSAs, so a second emitter placed DOWNSTREAM
 * is unavoidable. It emits the SAME `ReduxAction` event (via the shared
 * {@link buildReduxActionCoreAttrs}) so together the two make the event a
 * complete, one-row-per-dispatch spine carrying the monotonic `actionIndex`
 * every other telemetry stream joins on.
 *
 * It is observe-only: the action is never mutated. Rows carry `spineBackfill:
 * true` so consumers can separate them from the rich correlation rows; a plain
 * `count(*) … WHERE actionName = 'ReduxAction'` counts every non-ignored
 * dispatch. PLACEMENT: immediately after `redux-api-middleware`, before the tail
 * telemetry middlewares.
 */
export const createReduxActionSpineMiddleware = <TState = unknown>(
    config: ReduxActionSpineConfig<TState>
): Middleware<Record<string, never>, TState> => {
    const { enabled = false, storeId, getBusinessContext, ignoredActionPrefixes = TELEMETRY_IGNORED_ACTION_PREFIXES } = config;

    const isEnabled = (state: TState): boolean => {
        try {
            return typeof enabled === 'function' ? enabled(state) : enabled;
        } catch {
            return false;
        }
    };

    const safeBusinessContext = (state: TState): Record<string, unknown> => {
        if (!getBusinessContext) return {};
        try {
            return getBusinessContext(state) ?? {};
        } catch {
            return {};
        }
    };

    return (store) => (next) => (action) => {
        const state = store.getState();
        if (!isEnabled(state)) {
            return next(action);
        }
        if (!action) {
            return next(action);
        }
        const rawType = (action as { type?: unknown }).type;
        if (!rawType) {
            return next(action);
        }
        const actionType = String(rawType);
        if (matchesIgnoredActionPrefix(actionType, ignoredActionPrefixes)) {
            return next(action);
        }
        // correlationMiddleware (upstream) already emitted a rich ReduxAction
        // for anything it enriched — it stamps meta.actionIndex when it does. Only
        // fill the gap for actions that bypassed it (RSAA lifecycle FSAs).
        if ((action as { meta?: { actionIndex?: number } }).meta?.actionIndex != null) {
            return next(action);
        }

        const actionIndex = resolveJoinActionIndex(store, action);
        const correlationId = resolveJoinCorrelationId(action);
        safeEmitPageAction(REDUX_ACTION_SPINE_EVENT, {
            eventId: nanoid(),
            ...buildReduxActionCoreAttrs({
                storeId,
                sessionId: safeGetSessionId(),
                correlationId,
                actionIndex,
                actionType,
                actionStatus: getActionStatus(actionType),
                businessContext: safeBusinessContext(state)
            }),
            // Emit-time ordinal (US2056149) — see nextEmitSequence docs.
            emitSequence: nextEmitSequence(store),
            spineBackfill: true
        });

        return next(action);
    };
};
